import classNames from 'classnames';
import React from 'react';



type Props = {
	children: React.ReactNode,
	className?: string,
	/** Optional class for the scroll viewport (the scrollable area) */
	viewportClassName?: string,
};

const SCROLLBAR_WIDTH = 6;
const SCROLLBAR_PADDING = 2;

/**
 * Overlay scrollbar: renders a custom scrollbar that overlays content instead of
 * taking layout space. Prevents layout shift when scrollbar appears/disappears.
 */
const OverlayScrollbar: React.FC<Props> = (props) => {
	const {
		children,
		className,
		viewportClassName,
	} = props;

	const viewportRef = React.useRef<HTMLDivElement | null>(null);
	const trackRef = React.useRef<HTMLDivElement | null>(null);
	const thumbRef = React.useRef<HTMLDivElement | null>(null);
	const isDraggingRef = React.useRef(false);
	const dragStartYRef = React.useRef(0);
	const dragStartScrollTopRef = React.useRef(0);

	const [thumbHeight, setThumbHeight] = React.useState(0);
	const [thumbTop, setThumbTop] = React.useState(0);
	const [isVisible, setIsVisible] = React.useState(false);

	const updateThumb = React.useCallback(() => {
		const viewport = viewportRef.current;
		if (!viewport) {
			return;
		}

		const { scrollHeight, clientHeight, scrollTop } = viewport;
		const maxScroll = scrollHeight - clientHeight;

		if (maxScroll <= 0) {
			setIsVisible(false);
			return;
		}

		setIsVisible(true);
		const trackHeight = viewport.clientHeight;
		const ratio = clientHeight / scrollHeight;
		const thumbH = Math.max(20, trackHeight * ratio);
		const thumbTopValue = (scrollTop / maxScroll) * (trackHeight - thumbH - SCROLLBAR_PADDING * 2);
		setThumbHeight(thumbH);
		setThumbTop(thumbTopValue);
	}, []);

	React.useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) {
			return;
		}

		updateThumb();

		const resizeObserver = new ResizeObserver(updateThumb);
		resizeObserver.observe(viewport);

		const handleScroll = () => {
			if (!isDraggingRef.current) {
				updateThumb();
			}
		};

		viewport.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			resizeObserver.disconnect();
			viewport.removeEventListener('scroll', handleScroll);
		};
	}, [updateThumb, children]);

	const handleThumbMouseDown = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			isDraggingRef.current = true;
			dragStartYRef.current = e.clientY;
			dragStartScrollTopRef.current = viewportRef.current?.scrollTop ?? 0;
		},
		[],
	);

	React.useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDraggingRef.current) {
				return;
			}

			const viewport = viewportRef.current;
			if (!viewport) {
				return;
			}

			const deltaY = e.clientY - dragStartYRef.current;
			const maxScroll = viewport.scrollHeight - viewport.clientHeight;
			const trackHeight = viewport.clientHeight - SCROLLBAR_PADDING * 2;
			const scrollDelta = (deltaY / trackHeight) * maxScroll;
			viewport.scrollTop = Math.max(0, Math.min(maxScroll, dragStartScrollTopRef.current + scrollDelta));
			dragStartYRef.current = e.clientY;
			dragStartScrollTopRef.current = viewport.scrollTop;

			// Update thumb position during drag so it doesn't appear frozen
			updateThumb();
		};

		const handleMouseUp = () => {
			isDraggingRef.current = false;
			updateThumb();
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [updateThumb]);

	return (
		<div
			className={classNames('overlay-scrollbar', className)}
			style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}
		>
			<div
				className={classNames('overlay-scrollbar__viewport', viewportClassName)}
				ref={viewportRef}
				style={{
					overflowY: 'auto',
					overflowX: 'hidden',
					height: '100%',
					// Hide native scrollbar - no viewport padding; inner scrollbars get their own inset via CSS
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				}}
			>
				{children}
			</div>
			{/* Overlay scrollbar - positioned on top of content */}
			{isVisible && (
				<div
					aria-hidden={true}
					className="overlay-scrollbar__track"
					ref={trackRef}
					role="presentation"
					style={{
						position: 'absolute',
						top: 0,
						right: 0,
						bottom: 0,
						width: SCROLLBAR_WIDTH + SCROLLBAR_PADDING * 2,
						padding: `${SCROLLBAR_PADDING}px 0`,
						cursor: 'pointer',
						zIndex: 10,
					}}
				>
					<div
						className="overlay-scrollbar__thumb"
						onMouseDown={handleThumbMouseDown}
						ref={thumbRef}
						style={{
							position: 'absolute',
							right: SCROLLBAR_PADDING,
							width: SCROLLBAR_WIDTH,
							height: thumbHeight,
							top: SCROLLBAR_PADDING + thumbTop,
							borderRadius: 3,
							cursor: 'grab',
							pointerEvents: 'auto',
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default OverlayScrollbar;
